/**
 * Выбранное в «Отделке» стен: куда ляжет выбор и что там лежит сейчас — со всеми действиями
 * над этим, чтобы не прыгать в панель свойств. Компактно, в пару строк: стоит в плашке
 * внизу рядом с каталогом (`FinishTargetBar`), на телефоне — над каталогом.
 *
 * У стены — переключатель сторон, подписанный комнатами, в которые они смотрят (по умолчанию
 * сторона, по которой кликнули в 3D), и «На всю комнату». У краски и нарисованного кодом
 * рисунка — цвет: быстрые оттенки и свой. Картинку-текстуру не перекрашиваем — её цвет
 * задаёт сама картинка. Каждое изменение пишется в историю.
 */
import { WallFinishKind, finishItemById, wallFinishLook } from "@/shared/catalog";
import { useProjectStore } from "@/shared/store";
import { useWallFinishStore } from "@/shared/store/layers/wallFinishStore";
import { UIKit } from "@/shared/ui";
import { SelectionKind, useEditorStore } from "@/widgets/editor";
import { roomWallFaces } from "@/widgets/plan";
import type { FinishTarget } from "../hooks/useFinishTarget";
import { finishSwatchStyle } from "../logic";

/** Быстрые цвета краски — ходовые интерьерные оттенки. */
const PALETTE = [
	"#f4f2ee",
	"#ece4d6",
	"#d9cdb8",
	"#c9c3b8",
	"#b9c4b1",
	"#a9b8c4",
	"#c98f73",
	"#5c6166",
];

export const FinishTargetCard = ({ target }: { target: FinishTarget | null }) => {
	const isReadOnly = useEditorStore((s) => s.isReadOnly);
	const selection = useEditorStore((s) => s.selection);
	const setSelection = useEditorStore((s) => s.setSelection);
	const graph = useProjectStore((s) => s.project.layers.walls);
	const pushHistory = useProjectStore((s) => s.pushHistory);
	const setFinish = useWallFinishStore((s) => s.setFinish);
	const setFinishColor = useWallFinishStore((s) => s.setFinishColor);
	const removeFinish = useWallFinishStore((s) => s.removeFinish);

	const shown = target?.shown;
	const look = shown ? wallFinishLook(shown) : null;
	const current = target?.currentId ? finishItemById(target.currentId) : null;
	const swatchItem = look?.item ?? current;
	const editable = target !== null && !isReadOnly;
	const tintable = editable && look !== null && !look.item.texture;
	const wallSide = selection?.kind === SelectionKind.Wall ? target?.faces[0]?.side : undefined;

	const title = !target
		? "Кликните по стене или комнате"
		: (look?.item.name ??
			current?.name ??
			(target.anyFinish ? "Разная отделка - выбор ляжет на все" : "Без отделки"));

	const actions = editable && target.anyFinish && (
		<div className="flex shrink-0 gap-1">
			{target.sideRoom && shown && finishItemById(shown.finish) && (
				<button
					type="button"
					title={`Та же отделка на все стены: ${target.sides.find((s) => s.room === target.sideRoom)?.label}`}
					className="rounded-md border border-accent bg-accent-soft px-2 py-1 text-xs font-semibold text-accent hover:brightness-95"
					onClick={() => {
						if (!target.sideRoom) return;
						const roomFaces = roomWallFaces(graph, target.sideRoom);
						pushHistory();
						setFinish(roomFaces, shown.finish);
						setFinishColor(roomFaces, shown.color);
					}}
				>
					На всю комнату
				</button>
			)}
			<button
				type="button"
				title="Снять отделку"
				className="rounded-md border border-border bg-surface px-2 py-1 text-xs text-ink hover:bg-surface-hover"
				onClick={() => {
					pushHistory();
					removeFinish(target.faces);
				}}
			>
				Снять
			</button>
		</div>
	);

	return (
		<div className="flex flex-col gap-2 p-3">
			<div className="flex items-center gap-2.5">
				<UIKit.Text
					as="span"
					className="size-10 shrink-0 overflow-hidden rounded-md border border-border bg-surface-subtle"
					style={swatchItem ? finishSwatchStyle(swatchItem, look?.color, look?.accent) : undefined}
				>
					{swatchItem?.texture && (
						<img src={swatchItem.texture.thumb} alt="" className="size-full object-cover" />
					)}
				</UIKit.Text>
				<div className="min-w-0 flex-1">
					<UIKit.Text className="truncate text-xs text-muted">
						{target ? target.label : "Ничего не выделено"}
						{swatchItem?.collection ? ` · ${swatchItem.collection}` : ""}
					</UIKit.Text>
					<UIKit.Text className="truncate text-sm font-semibold text-ink">{title}</UIKit.Text>
				</div>
				{actions}
			</div>

			{/* Сторона стены: какая из двух граней получит отделку */}
			{target && target.sides.length > 0 && wallSide !== undefined && selection && (
				<UIKit.SegmentedControl
					value={wallSide}
					options={target.sides.map((s) => ({
						value: s.side,
						label: s.label,
						title: s.room ? `Сторона, которая смотрит в ${s.label}` : "Наружная сторона",
					}))}
					onChange={(next) =>
						setSelection({ kind: SelectionKind.Wall, id: selection.id, side: next })
					}
				/>
			)}

			{/* Цвет: у картинки его задаёт сама картинка */}
			{tintable && look && (
				<div
					className="flex flex-wrap items-center gap-1"
					// История пишется один раз на выбор цвета, а не на каждое движение ползунка
					onPointerDown={() => pushHistory()}
				>
					{PALETTE.map((color) => (
						<button
							key={color}
							type="button"
							title={color}
							aria-label={`Цвет ${color}`}
							className={`size-6 rounded-md border ${
								look.color === color ? "border-accent ring-2 ring-accent-soft" : "border-border"
							}`}
							style={finishSwatchStyle(look.item, color, color)}
							onClick={() => setFinishColor(target.faces, color)}
						/>
					))}
					{shown?.color && (
						<button
							type="button"
							title="Цвет как в каталоге"
							className="rounded-md px-1.5 py-1 text-xs text-muted hover:bg-surface-hover hover:text-ink"
							onClick={() => setFinishColor(target.faces, undefined)}
						>
							Сброс
						</button>
					)}
					<div className="ml-auto">
						<UIKit.ColorPicker
							up
							label={look.item.kind === WallFinishKind.Paint ? "Свой цвет" : "Свой основной"}
							value={look.color}
							onChange={(color) => setFinishColor(target.faces, color)}
						/>
					</div>
				</div>
			)}
		</div>
	);
};
